import { create } from 'zustand';
import { fixEncoding } from '../../../utils/fixMojibake';

// GQ Search Assistant chat store.
//
// Talks to the GQ Search backend that lives inside the jnrb Spring Boot service
// (com.gq.nrb GqSearch* under /jnrb/api/search/*) — built the same way as GQ Support,
// so there is no separate service. REACT_APP_GQ_SEARCH_API is the jnrb base
// (e.g. "http://localhost/GQ/jnrb" in dev, ".../jnrb" on stage/prod); the store hits
// `${API}/api/search/conversations*`.
//
// Auth mirrors GQ Support: the logged-in user's email is sent as X-User-Id (set via
// configureGQSearch on load). The GenomeQuest API token — kept in memory only — is
// sent as X-GQ-ApiToken and forwarded by the backend to the flow's MCP tools.
//
// The exchange is synchronous (one request → the full assistant reply); `streaming`
// is used only to drive the "typing" indicator while the request is in flight.
const API = process.env.REACT_APP_GQ_SEARCH_API || '';

// Remember the open conversation across a page refresh (per-tab). Without this, a
// reload resets activeConversationId to null and drops you into a new empty search.
const ACTIVE_CONV_KEY = 'gqsearch.activeConversationId';

// User identity for X-User-Id — set once from GQSearch.jsx via AccountService.
let _userEmail = 'anonymous@aptean.com';
export function configureGQSearch(email) {
  _userEmail = email || 'anonymous@aptean.com';
}

/** Map a backend MessageOutDto {id,role,text,workflowId} to the UI message shape. */
function mapMessage(m) {
  return {
    id: m.id,
    role: m.role,
    content: m.text ?? '',
    workflowId: m.workflowId || undefined,
    createdAt: new Date().toISOString(),
  };
}

export const useChatStore = create((set, get) => {
  const headers = () => {
    const h = { 'Content-Type': 'application/json', 'X-User-Id': _userEmail };
    const token = get().gqApiToken;
    if (token) h['X-GQ-ApiToken'] = token;
    return h;
  };

  return {
    conversations: [],
    activeConversationId: null,
    messages: [],
    streaming: false,
    streamingContent: '',
    // GQ API token — kept in memory only, never persisted.
    gqApiToken: '',

    setGqApiToken: (token) => set({ gqApiToken: token }),

    loadConversations: async () => {
      try {
        const res = await fetch(`${API}/api/search/conversations`, { headers: headers() });
        if (!res.ok) throw new Error('Failed to load conversations');
        const data = fixEncoding(await res.json());
        set({ conversations: data });
      } catch (e) {
        console.error('loadConversations:', e);
      }
    },

    selectConversation: async (id) => {
      try {
        const res = await fetch(`${API}/api/search/conversations/${id}`, { headers: headers() });
        if (!res.ok) return;
        const conv = fixEncoding(await res.json());
        set({
          activeConversationId: id,
          messages: (conv.messages ?? []).map(mapMessage),
        });
        try { sessionStorage.setItem(ACTIVE_CONV_KEY, id); } catch (e) { /* ignore */ }
      } catch (e) {
        console.error('selectConversation:', e);
      }
    },

    // Re-open the conversation that was active before a page refresh. Called on mount;
    // if the stored id was deleted, selectConversation just no-ops (404).
    restoreConversation: () => {
      let id;
      try { id = sessionStorage.getItem(ACTIVE_CONV_KEY); } catch (e) { /* ignore */ }
      if (id) get().selectConversation(id);
    },

    newConversation: () => {
      try { sessionStorage.removeItem(ACTIVE_CONV_KEY); } catch (e) { /* ignore */ }
      set({ activeConversationId: null, messages: [] });
    },

    deleteConversation: async (id) => {
      try {
        await fetch(`${API}/api/search/conversations/${id}`, { method: 'DELETE', headers: headers() });
      } catch (e) {
        console.error('deleteConversation:', e);
      }
      try {
        if (sessionStorage.getItem(ACTIVE_CONV_KEY) === id) sessionStorage.removeItem(ACTIVE_CONV_KEY);
      } catch (e) { /* ignore */ }
      set((s) => ({
        conversations: s.conversations.filter((c) => c.id !== id),
        activeConversationId: s.activeConversationId === id ? null : s.activeConversationId,
        messages: s.activeConversationId === id ? [] : s.messages,
      }));
    },

    sendMessage: async (text) => {
      // Optimistically add the user message.
      const tempUserMsg = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
      };
      set((s) => ({ messages: [...s.messages, tempUserMsg], streaming: true, streamingContent: '' }));

      try {
        // A conversation row must exist before posting a message (posting to a
        // client-invented id would 404), so create one on first turn.
        let convId = get().activeConversationId;
        if (!convId) {
          const createRes = await fetch(`${API}/api/search/conversations`, {
            method: 'POST',
            headers: headers(),
          });
          if (!createRes.ok) throw new Error('Failed to start conversation');
          const created = await createRes.json();
          convId = created.id;
          set({ activeConversationId: convId });
          try { sessionStorage.setItem(ACTIVE_CONV_KEY, convId); } catch (e) { /* ignore */ }
        }

        const res = await fetch(`${API}/api/search/conversations/${convId}/messages`, {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ text }),
        });
        if (!res.ok) throw new Error(`Search backend error ${res.status}`);
        const answer = fixEncoding(await res.json());

        set((s) => ({
          messages: [...s.messages, mapMessage(answer)],
          streaming: false,
          streamingContent: '',
        }));

        // Refresh the list so the title / workflow badge / hit count update.
        await get().loadConversations();
      } catch (e) {
        const errMsg = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `⚠️ Error: ${e.message}`,
          createdAt: new Date().toISOString(),
        };
        set((s) => ({ messages: [...s.messages, errMsg], streaming: false, streamingContent: '' }));
        // The backend titles the search from the first question before it runs, so
        // refresh the list even on error so the sidebar shows the question as the name.
        await get().loadConversations();
      }
    },
  };
});
